import { blockedWpExactPaths, blockedWpPathPrefixes } from './blocked-wp-paths';
import { CrawlerPageStore } from './crawler-page-store';
import { RulesViewIdentityGuard } from './rules-view-identity-guard';
import { CrawlEngine } from './crawl-engine';
import { IframeTestRunner } from './iframe-test-runner';
import { CommonLayoutDetector } from './common-layout-detector';
import { RulePartClassifier } from './rule-part-classifier';

function isSubmitSampleForceEnabled() {
	const forceFlag = window?.a11yfixData?.forceShowSubmitSample;

	if (typeof forceFlag === 'boolean') {
		return forceFlag;
	}

	if (typeof forceFlag === 'number') {
		return forceFlag === 1;
	}

	if (typeof forceFlag === 'string') {
		const normalized = forceFlag.trim().toLowerCase();
		return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
	}

	return false;
}

export class A11yFixUrlCrawler {
	constructor(localStorageService, i18n, layout, ruleRunner) {
		this.localStorageService = localStorageService;
		this.__ = i18n;
		this.layout = layout;
		this.ruleRunner = ruleRunner;
		this.blockedWpPathPrefixes = blockedWpPathPrefixes;
		this.blockedWpExactPaths = blockedWpExactPaths;
		this.maxPages = 300;
		this.crawledPathsStorageKey = 'crawled_paths_v1';
		this.rulesResultsStorageKey = 'crawled_pages_v1';
		this.rulesResultsDetailsStorageKey = 'crawled_pages_details_v1';
		this.rulesResultsDetailsIndexStorageKey = `${this.rulesResultsDetailsStorageKey}__keys`;
		this.layoutDetectionStorageKey = 'layout_detection_v1';
		this.homeUrl = window?.a11yfixData?.homeUrl || window.location.origin;
		this.pageStore = new CrawlerPageStore(
			this.localStorageService,
			this.ruleRunner,
			() => this.homeUrl,
			this.blockedWpPathPrefixes,
			this.blockedWpExactPaths
		);
		this.homeUrl = this.pageStore.normalizeUrl(this.homeUrl, window.location.origin);
		this.rulesViewIdentityGuard = new RulesViewIdentityGuard(
			this.layout,
			this.__,
			(value) => this.pageStore.normalizePostId(value),
			(rawUrl, baseUrl) => this.pageStore.normalizeUrl(rawUrl, baseUrl)
		);
		this.crawlEngine = new CrawlEngine({
			maxPages: this.maxPages,
			fetchPage: (url) => this.fetchPage(url),
			extractLinks: (html, baseUrl) => this.pageStore.extractLinks(html, baseUrl),
			normalizePostId: (value) => this.pageStore.normalizePostId(value)
		});
		this.iframeTestRunner = new IframeTestRunner({
			ruleRunner: this.ruleRunner,
			layout: this.layout,
			normalizePostId: (value) => this.pageStore.normalizePostId(value)
		});
		this.commonLayoutDetector = new CommonLayoutDetector();
		this.rulePartClassifier = new RulePartClassifier();
		this.isRunning = false;
		this.shouldStopIframeRun = false;
		this.hasRulesCheckStarted = false;
		this.crawledPages = [];
		this.ruleResultPages = [];
		this.layoutDetection = this.getDefaultLayoutDetection();
		this.pages = [];
		this.rulesViewMapHistory = [];
		this.lastRulesViewMapSnapshot = null;
		this.lastLiveToEditorBlockMap = null;
		this.currentRulesViewPage = null;
		this.rulesViewLiveUrl = '';
		this.rulesViewEditorUrl = '';
		this.isSubmittingSample = false;
		this.ruleDataPrefetchInFlightByUrl = new Map();
		this.ruleDataPrefetchTimerId = 0;
	}

	async init() {
		if (!this.layout || !this.layout.isReady()) {
			return;
		}

		await this.localStorageService.hydrateKeys([
			this.crawledPathsStorageKey,
			this.rulesResultsStorageKey,
			this.rulesResultsDetailsIndexStorageKey,
			this.layoutDetectionStorageKey
		]);

		this.crawledPages = this.pageStore.loadCrawledPages(this.crawledPathsStorageKey, this.rulesResultsStorageKey);
		this.ruleResultPages = this.pageStore.loadRuleResultPages(this.rulesResultsStorageKey);
		this.crawledPages = this.pageStore.syncCrawledPagesWithRulePostIds(this.crawledPages, this.ruleResultPages);
		this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
		this.layoutDetection = this.loadLayoutDetection();
		this.hasRulesCheckStarted = this.ruleResultPages.length > 0;
		this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);

		if (this.hasRulesCheckStarted) {
			await this.restorePersistedRuleData();
		}

		this.bindEvents();
		this.layout.setCommonLayoutDetection(this.layoutDetection);
		this.layout.renderCrawledPages(this.crawledPages);
		this.layout.renderPagesWithRules(this.hasRulesCheckStarted ? this.pages : []);
		if (this.hasRulesCheckStarted) {
			this.scheduleRuleDataPrefetch(5);
		}
		this.updateSubmitSampleButtonVisibility();
		this.layout.setStatus(this.__( 'Ready', 'a11yfix' ));
	}

	async restorePersistedRuleData() {
		if (!Array.isArray(this.pages) || this.pages.length === 0) {
			return;
		}

		const restoredPages = await Promise.all(this.pages.map(async (page) => {
			if (!page || this.pageStore.hasRuleDataBundle(page)) {
				return page;
			}

			const loadedRuleDataBundle = await this.pageStore.loadRuleDataBundleForPage(this.rulesResultsDetailsStorageKey, page.url);

			if (!loadedRuleDataBundle) {
				return page;
			}

			return {
				...page,
				ruleDetails: loadedRuleDataBundle.ruleDetails || {},
				failedSelectorsByRule: loadedRuleDataBundle.failedSelectorsByRule || {},
				rulePartsByRule: loadedRuleDataBundle.rulePartsByRule || {}
			};
		}));

		this.pages = restoredPages;
		this.ruleResultPages = this.ruleRunner.normalizePages(restoredPages);
		this.pageStore.saveRuleResultPages(this.rulesResultsStorageKey, restoredPages, this.rulesResultsDetailsStorageKey);
	}

	scheduleRuleDataPrefetch(limit = 5) {
		window.clearTimeout(this.ruleDataPrefetchTimerId);

		const runPrefetch = () => {
			void this.prefetchRuleDataForFirstPages(limit);
		};

		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(() => {
				runPrefetch();
			}, { timeout: 900 });
			return;
		}

		this.ruleDataPrefetchTimerId = window.setTimeout(runPrefetch, 160);
	}

	async prefetchRuleDataForFirstPages(limit = 5) {
		const prefetchLimit = Number.isInteger(limit) && limit > 0 ? limit : 5;
		const pageIndexes = [];
		const seenUrls = new Set();

		for (let pageIndex = 0; pageIndex < this.pages.length; pageIndex += 1) {
			if (pageIndexes.length >= prefetchLimit) {
				break;
			}

			const page = this.pages[pageIndex];

			if (!page || this.pageStore.hasRuleDataBundle(page)) {
				continue;
			}

			const normalizedPageUrl = this.pageStore.normalizeUrl(page.url);

			if (!normalizedPageUrl || seenUrls.has(normalizedPageUrl) || this.ruleDataPrefetchInFlightByUrl.has(normalizedPageUrl)) {
				continue;
			}

			seenUrls.add(normalizedPageUrl);
			pageIndexes.push({
				index: pageIndex,
				url: normalizedPageUrl
			});
		}

		if (pageIndexes.length === 0) {
			return;
		}

		await Promise.all(pageIndexes.map(async (entry) => {
			this.ruleDataPrefetchInFlightByUrl.set(entry.url, true);

			try {
				await this.ensurePageRuleDataLoaded(entry.index);
			} finally {
				this.ruleDataPrefetchInFlightByUrl.delete(entry.url);
			}
		}));
	}

	bindEvents() {
		this.layout.bindEvents({
			onStart: () => {
				this.start();
			},
			onRunIframe: () => {
				this.runCrawledPagesInIframe();
			},
			onRunIframeAll: () => {
				this.runCrawledPagesInIframe();
			},
			onRunIframeCurrent: () => {
				this.runCurrentIframePageInIframe();
			},
			onStopTest: () => {
				this.stopIframeTest();
			},
			onClearCrawled: () => {
				this.clearCrawledPages();
			},
			onClearRules: () => {
				this.clearRulesResults();
			},
			onToggleRuleHighlight: (ruleKey) => {
				this.toggleRulesViewRule(ruleKey);
			},
			onRuleDetails: (pageIndex, ruleKey) => {
				this.openRuleDetails(pageIndex, ruleKey);
			},
			onRuleUrlOpen: (pageIndex) => {
				this.openRulesViewByPageIndex(pageIndex);
			},
			onCrawledPageUrlOpen: (pageIndex) => {
				this.openCrawledPageInIframe(pageIndex);
			},
			onRulesViewModeSwitch: () => {
				this.switchRulesViewMode();
			},
			onRetestRulesViewPage: () => {
				this.retestCurrentRulesViewPage();
			},
			onViewSample: () => {
				this.viewRulesViewSample();
			},
			onSubmitSample: () => {
				this.submitRulesViewSample();
			}
		});

		this.layout.bindRulesViewIframeNavigation(async (navigationData) => {
			const isValidIdentity = this.rulesViewIdentityGuard.handleNavigation(navigationData);

			if (!isValidIdentity) {
				return;
			}

			const snapshot = await this.captureRulesViewMapSnapshot(navigationData, 'navigation');
			this.refreshRulesViewModeSwitchState(navigationData, snapshot);
		});

		this.layout.bindPreviewIframeNavigation((navigationData) => {
			this.syncTestTabButtonsForNavigation(navigationData);
		});

		this.syncTestTabButtonsForNavigation(this.layout.getPreviewIframeNavigationData());
	}

	async ensurePageRuleDataLoaded(pageIndex) {
		const page = this.pages[pageIndex];

		if (!page) {
			return null;
		}

		if (this.pageStore.hasRuleDataBundle(page)) {
			return page;
		}

		const loadedRuleDataBundle = await this.pageStore.loadRuleDataBundleForPage(this.rulesResultsDetailsStorageKey, page.url);

		if (!loadedRuleDataBundle) {
			return page;
		}

		const enrichedPage = {
			...page,
			ruleDetails: loadedRuleDataBundle.ruleDetails || {},
			failedSelectorsByRule: loadedRuleDataBundle.failedSelectorsByRule || {},
			rulePartsByRule: loadedRuleDataBundle.rulePartsByRule || {}
		};

		this.pages[pageIndex] = enrichedPage;

		const normalizedTargetUrl = this.pageStore.normalizeUrl(enrichedPage.url);
		this.ruleResultPages = (this.ruleResultPages || []).map((rulePage) => {
			if (this.pageStore.normalizeUrl(rulePage?.url || '') !== normalizedTargetUrl) {
				return rulePage;
			}

			return enrichedPage;
		});

		return enrichedPage;
	}

	syncTestTabButtonsForNavigation(navigationData) {
		const isAllowed = this.isPreviewIframeUrlInCrawledPages(navigationData?.url || '');
		this.layout.setTestTabActionsEnabled(isAllowed);
	}

	isPreviewIframeUrlInCrawledPages(rawUrl) {
		const normalizedPreviewUrl = this.pageStore.normalizeUrl(rawUrl, window.location.origin);

		if (!normalizedPreviewUrl || normalizedPreviewUrl === 'about:blank') {
			return false;
		}

		return this.crawledPages.some((page) => {
			return this.pageStore.normalizeUrl(page?.url || '', window.location.origin) === normalizedPreviewUrl;
		});
	}

	switchRulesViewMode() {
		const navigationData = this.layout.getRulesViewIframeNavigationData();
		const mode = this.resolveRulesViewMode(navigationData || {}, null);
		this.layout.setSubmitSampleButtonState(false, {
			hint: ''
		});

		if (mode === 'block-editor') {
			if (!this.rulesViewLiveUrl) {
				this.layout.setStatusError(this.__( 'Live view URL is not available yet.', 'a11yfix' ));
				return;
			}

			this.layout.setStatus(this.__( 'Switching to live view...', 'a11yfix' ));
			this.layout.loadRulesViewUrl(this.rulesViewLiveUrl);
			return;
		}

		if (!this.rulesViewEditorUrl) {
			this.layout.setStatus(this.__( 'WP editor view is not available for this page.', 'a11yfix' ));
			return;
		}

		this.layout.setStatus(this.__( 'Switching to WP editor view...', 'a11yfix' ));
		this.layout.loadRulesViewUrl(this.rulesViewEditorUrl);
	}

	toggleRulesViewRule(ruleKey) {
		const toggleResult = this.layout.toggleRulesViewRule(ruleKey);

		if (!toggleResult.ready) {
			this.layout.setStatus(this.__( 'Rules view is not ready yet.', 'a11yfix' ));
			return;
		}

		if (!toggleResult.hasSelectors) {
			this.layout.setStatus(this.__( 'No failed selectors available for this rule.', 'a11yfix' ));
			return;
		}

		let mappedHighlightsCount = null;
		const navigationData = this.layout.getRulesViewIframeNavigationData();
		const mode = this.resolveRulesViewMode(navigationData || {}, null);

		if (mode === 'block-editor') {
			const latestFrontendSnapshot = this.getLatestRulesViewMapSnapshotByMode('frontend');

			if (latestFrontendSnapshot?.map) {
				const liveToEditorBlockMap = this.layout.mapLiveBlocksToEditorAndHighlight(latestFrontendSnapshot.map);
				mappedHighlightsCount = Number.isInteger(liveToEditorBlockMap?.highlightedMatchedCount)
					? liveToEditorBlockMap.highlightedMatchedCount
					: 0;
				this.lastLiveToEditorBlockMap = liveToEditorBlockMap;
			}
		}

		const visibleHighlightsCount = mappedHighlightsCount !== null ? mappedHighlightsCount : toggleResult.highlightedCount;

		if (toggleResult.isActive) {
			this.layout.setStatus(`${this.__( 'Rule highlights shown. Visible highlighted elements:', 'a11yfix' )} ${visibleHighlightsCount}`);
			return;
		}

		this.layout.setStatus(`${this.__( 'Rule highlights updated. Visible highlighted elements:', 'a11yfix' )} ${visibleHighlightsCount}`);
	}

	async retestCurrentRulesViewPage() {
		if (this.isRunning) {
			return;
		}

		if (!this.ruleRunner) {
			this.layout.setStatus(this.__( 'Rule runner is not configured.', 'a11yfix' ));
			return;
		}

		const navigationData = this.layout.getRulesViewIframeNavigationData();
		const currentIframeUrl = this.pageStore.normalizeUrl(
			navigationData?.url || this.layout?.rulesPreviewIframe?.src || '',
			window.location.origin
		);
		const isEditorMode = this.resolveRulesViewMode(navigationData || {}, null) === 'block-editor';
		const targetLiveUrl = this.pageStore.normalizeUrl(
			this.currentRulesViewPage?.url || (!isEditorMode ? currentIframeUrl : ''),
			window.location.origin
		);

		if (!targetLiveUrl || targetLiveUrl === 'about:blank') {
			this.layout.setStatus(this.__( 'Open a page in Rules view from Test results before retesting.', 'a11yfix' ));
			return;
		}

		const fallbackPage = {
			url: targetLiveUrl,
			depth: 0,
			postId: null,
			pageType: 'singular',
			layoutSignals: null,
			rules: {},
			ruleDetails: {},
			failedSelectorsByRule: {},
			rulePartsByRule: {}
		};
		const targetPage = this.pages.find((page) => this.pageStore.normalizeUrl(page?.url || '') === targetLiveUrl)
			|| this.crawledPages.find((page) => this.pageStore.normalizeUrl(page?.url || '') === targetLiveUrl)
			|| this.currentRulesViewPage
			|| fallbackPage;

		this.isRunning = true;
		this.shouldStopIframeRun = false;
		this.hasRulesCheckStarted = true;
		this.layout.setBusyState(true);
		this.layout.setTestingState(true);
		this.layout.setStatus(this.__( 'Retesting current Rules view page...', 'a11yfix' ));

		try {
			const loadResult = await this.layout.loadRulesViewUrl(targetLiveUrl);
			const normalizedPostId = this.pageStore.normalizePostId(loadResult?.postId ?? targetPage?.postId);
			const pageForTest = {
				...targetPage,
				url: targetLiveUrl,
				postId: normalizedPostId
			};
			const updatedPageBase = await this.ruleRunner.runOnDocument(pageForTest, loadResult?.documentNode);
			const rulePartsByRule = this.rulePartClassifier.classifyPageRuleParts(updatedPageBase, loadResult?.documentNode);
			const updatedPage = {
				...updatedPageBase,
				rulePartsByRule
			};

			const existingRulePages = this.ruleRunner.normalizePages(this.ruleResultPages || []);
			const updatedRulePages = existingRulePages.map((page) => {
				if (this.pageStore.normalizeUrl(page.url) !== targetLiveUrl) {
					return page;
				}

				return updatedPage;
			});

			if (!updatedRulePages.some((page) => this.pageStore.normalizeUrl(page.url) === targetLiveUrl)) {
				updatedRulePages.push(updatedPage);
			}

			this.ruleResultPages = this.ruleRunner.normalizePages(updatedRulePages);
			this.crawledPages = this.pageStore.syncCrawledPagesWithRulePostIds(this.crawledPages, this.ruleResultPages);
			this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
			this.layout.renderCrawledPages(this.crawledPages);
			this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
			this.pageStore.saveRuleResultPages(this.rulesResultsStorageKey, this.pages, this.rulesResultsDetailsStorageKey);
			this.layout.renderPagesWithRules(this.pages);
			this.scheduleRuleDataPrefetch(5);
			this.currentRulesViewPage = updatedPage;

			const updatedPageIndex = this.pages.findIndex((page) => this.pageStore.normalizeUrl(page?.url || '') === targetLiveUrl);

			if (updatedPageIndex >= 0) {
				await this.openRulesViewByPageIndex(updatedPageIndex);
				this.layout.setStatus(this.__( 'Page retested and refreshed in Rules view.', 'a11yfix' ));
				return;
			}

			const refreshUrl = currentIframeUrl && currentIframeUrl !== 'about:blank' ? currentIframeUrl : targetLiveUrl;
			await this.layout.loadRulesViewUrl(refreshUrl);
			this.layout.setStatus(this.__( 'Page retested and refreshed in Rules view.', 'a11yfix' ));
		} catch (error) {
			this.layout.setStatus(
				`${this.__( 'Iframe run failed:', 'a11yfix' )} ${error instanceof Error ? error.message : this.__( 'Unknown error', 'a11yfix' )}`
			);
		} finally {
			this.isRunning = false;
			this.shouldStopIframeRun = false;
			this.layout.setTestingState(false);
			this.layout.setBusyState(false);
		}
	}

	async openRulesViewByPageIndex(pageIndex) {
		const page = await this.ensurePageRuleDataLoaded(pageIndex);

		if (!page) {
			return;
		}

		const failedSelectorsByRule = page.failedSelectorsByRule || (this.ruleRunner ? this.ruleRunner.getFailedSelectorsMap(page) : {});
		this.currentRulesViewPage = page;
		this.rulesViewLiveUrl = page.url || '';
		this.rulesViewEditorUrl = '';
		this.layout.setRulesViewModeSwitchState('frontend', false);

		this.layout.showRulesViewTab();
		this.layout.clearRulesViewIframe();
		this.rulesViewIdentityGuard.setExpectedIdentity(page);
		this.layout.setStatus(this.__( 'Loading rules view...', 'a11yfix' ));

		const loadResult = await this.layout.loadRulesViewUrl(page.url);

		if (!loadResult.loaded) {
			this.layout.setStatus(this.__( 'Rules view could not be loaded.', 'a11yfix' ));
			return;
		}

		this.layout.setRulesViewSelectorsByRule(failedSelectorsByRule, page);
		if (!this.rulesViewIdentityGuard.validateIdentity(loadResult.postId, page.url)) {
			return;
		}

		const initialNavigationData = {
			url: loadResult?.documentNode?.location?.href || page.url,
			postId: loadResult?.postId || page.postId || null
		};

		const initialSnapshot = await this.captureRulesViewMapSnapshot(
			{
				url: initialNavigationData.url,
				postId: initialNavigationData.postId
			},
			'initial-load'
		);
		this.refreshRulesViewModeSwitchState(initialNavigationData, initialSnapshot);

		const selectorsTotal = Object.values(failedSelectorsByRule || {}).reduce(
			(total, selectors) => total + (Array.isArray(selectors) ? selectors.length : 0),
			0
		);

		let highlightedCount = this.layout.applyRulesHighlightsFromActiveRules();

		if (highlightedCount === 0 && selectorsTotal > 0) {
			await new Promise((resolve) => {
				window.setTimeout(resolve, 700);
			});

			highlightedCount = this.layout.applyRulesHighlightsFromActiveRules(false);
		}

		if (highlightedCount === 0) {
			const message = this.__( 'Rules view loaded. No failed selectors found on the page.', 'a11yfix' );
			this.layout.setStatus(message);
			return;
		}

		const message = `${this.__( 'Rules view loaded. Highlighted elements:', 'a11yfix' )} ${highlightedCount}`;
		this.layout.setStatus(message);
	}

	refreshRulesViewModeSwitchState(navigationData, snapshot) {
		const mode = this.resolveRulesViewMode(navigationData || {}, snapshot || null);

		if (mode === 'block-editor') {
			this.layout.setRulesViewModeSwitchState('block-editor', true);
			this.updateSubmitSampleButtonVisibility(navigationData, snapshot);
			return;
		}

		const editorUrl = this.resolveRulesViewEditorUrl(navigationData, snapshot);
		this.rulesViewEditorUrl = editorUrl;
		this.layout.setRulesViewModeSwitchState('frontend', Boolean(editorUrl));
		this.layout.setSubmitSampleButtonState(false, {
			hint: ''
		});
	}

	getSubmitSampleEnabledHint() {
		return this.__( 'We detected some blocks displacement, can you send us anonymous data', 'a11yfix' );
	}

	resolveRulesViewEditorUrl(navigationData, snapshot) {
		const parsedPostId = Number.parseInt(String(navigationData?.postId || this.currentRulesViewPage?.postId), 10);
		const postId = Number.isInteger(parsedPostId) && parsedPostId > 0 ? parsedPostId : null;

		if (!postId || !this.isLikelyGutenbergPage(snapshot)) {
			return '';
		}

		return this.buildWpPostEditorUrl(postId);
	}

	isLikelyGutenbergPage(snapshot) {
		if (!snapshot || snapshot.mode !== 'frontend') {
			return false;
		}

		return Number.isInteger(snapshot.totalBlocks) && snapshot.totalBlocks > 0;
	}

	buildWpPostEditorUrl(postId) {
		const adminUrl = String(window?.a11yfixData?.adminUrl || '').trim();

		if (!adminUrl) {
			return '';
		}

		try {
			const url = new URL('post.php', adminUrl);
			url.searchParams.set('post', String(postId));
			url.searchParams.set('action', 'edit');
			return url.toString();
		} catch {
			return '';
		}
	}

	resolveRulesViewMode(navigationData, mapResult) {
		const url = String(navigationData?.url || mapResult?.pageUrl || '').toLowerCase();

		if (url.includes('/wp-admin/post.php') || url.includes('/wp-admin/post-new.php') || url.includes('/wp-admin/site-editor.php')) {
			return 'block-editor';
		}

		return 'frontend';
	}

	getLatestRulesViewMapSnapshotByMode(mode) {
		if (!Array.isArray(this.rulesViewMapHistory) || typeof mode !== 'string' || mode.length === 0) {
			return null;
		}

		for (let index = this.rulesViewMapHistory.length - 1; index >= 0; index -= 1) {
			const snapshot = this.rulesViewMapHistory[index];

			if (snapshot?.mode === mode) {
				return snapshot;
			}
		}

		return null;
	}

	async captureRulesViewMapSnapshot(navigationData, source = 'navigation') {
		let wpBlocksMapResult = this.layout.buildRulesViewWpBlocksMap();

		if (wpBlocksMapResult?.ok === true && wpBlocksMapResult?.totalBlocks === 0) {
			for (let attempt = 1; attempt <= 3; attempt += 1) {
				await new Promise((resolve) => {
					window.setTimeout(resolve, 500);
				});

				wpBlocksMapResult = this.layout.buildRulesViewWpBlocksMap();

				if (wpBlocksMapResult?.totalBlocks > 0) {
					break;
				}
			}
		}

		const mode = this.resolveRulesViewMode(navigationData, wpBlocksMapResult);
		const editorModeDivsResult = null;

		const latestFrontendSnapshot = mode === 'block-editor'
			? this.getLatestRulesViewMapSnapshotByMode('frontend')
			: null;
		const liveToEditorBlockMap = mode === 'block-editor' && latestFrontendSnapshot?.map
			? this.layout.mapLiveBlocksToEditorAndHighlight(latestFrontendSnapshot.map)
			: null;



		const snapshot = {
			capturedAt: new Date().toISOString(),
			source,
			mode,
			url: navigationData?.url || wpBlocksMapResult?.pageUrl || '',
			postId: this.pageStore.normalizePostId(navigationData?.postId),
			ok: wpBlocksMapResult?.ok === true,
			totalBlocks: Number.isInteger(wpBlocksMapResult?.totalBlocks) ? wpBlocksMapResult.totalBlocks : 0,
			blockTypesCount: Number.isInteger(wpBlocksMapResult?.blockTypesCount) ? wpBlocksMapResult.blockTypesCount : 0,
			targetDocumentsCount: Number.isInteger(wpBlocksMapResult?.targetDocumentsCount) ? wpBlocksMapResult.targetDocumentsCount : 0,
			excludedHeaderBlocks: Number.isInteger(wpBlocksMapResult?.excludedHeaderBlocks) ? wpBlocksMapResult.excludedHeaderBlocks : 0,
			excludedFooterBlocks: Number.isInteger(wpBlocksMapResult?.excludedFooterBlocks) ? wpBlocksMapResult.excludedFooterBlocks : 0,
			editorModeDivsCount: Number.isInteger(editorModeDivsResult?.count) ? editorModeDivsResult.count : 0,
			liveBlocksComparedCount: Number.isInteger(liveToEditorBlockMap?.liveBlocksCount) ? liveToEditorBlockMap.liveBlocksCount : 0,
			editorBlocksComparedCount: Number.isInteger(liveToEditorBlockMap?.editorBlocksCount) ? liveToEditorBlockMap.editorBlocksCount : 0,
			matchedLiveToEditorBlocksCount: Number.isInteger(liveToEditorBlockMap?.matchedCount) ? liveToEditorBlockMap.matchedCount : 0,
			map: wpBlocksMapResult
		};

		this.rulesViewMapHistory.push(snapshot);
		this.lastRulesViewMapSnapshot = snapshot;
		this.layout.recordRulesViewMapSnapshot(snapshot);
		if (liveToEditorBlockMap) {
			this.lastLiveToEditorBlockMap = liveToEditorBlockMap;
		}

		this.updateSubmitSampleButtonVisibility(navigationData, snapshot);

		return snapshot;
	}

	updateSubmitSampleButtonVisibility(navigationData = null, snapshot = null, mapping = null) {
		if (!this.layout || typeof this.layout.setSubmitSampleButtonState !== 'function') {
			return;
		}

		if (isSubmitSampleForceEnabled()) {
			this.layout.setSubmitSampleButtonState(true, {
				disabled: false,
				title: this.__( 'Test mode: submit sample button is force-enabled.', 'a11yfix' ),
				hint: this.getSubmitSampleEnabledHint()
			});
			return;
		}

		const safeNavigationData = navigationData || this.layout.getRulesViewIframeNavigationData() || {};
		const currentNavigationData = this.layout.getRulesViewIframeNavigationData() || {};
		const safeSnapshot = snapshot || this.lastRulesViewMapSnapshot || null;
		const mode = this.resolveRulesViewMode(safeNavigationData, safeSnapshot?.map || null);
		const currentMode = this.resolveRulesViewMode(currentNavigationData, null);

		if (mode !== 'block-editor' || currentMode !== 'block-editor') {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		const frontendSnapshot = this.getLatestRulesViewMapSnapshotByMode('frontend');
		const editorSnapshot = this.getLatestRulesViewMapSnapshotByMode('block-editor');
		const safeMapping = mapping || this.lastLiveToEditorBlockMap || null;
		const readiness = this.buildRulesViewSampleReadiness(frontendSnapshot, editorSnapshot, safeMapping);

		if (!readiness.isReady || !safeMapping || safeMapping.ok === false) {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		const matchedCount = Number.isInteger(safeMapping.matchedCount) ? safeMapping.matchedCount : 0;
		const liveBlocksCount = Number.isInteger(safeMapping.liveBlocksCount) ? safeMapping.liveBlocksCount : 0;

		if (liveBlocksCount <= 0) {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		if (matchedCount <= 0) {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		if (matchedCount >= liveBlocksCount) {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		const isUseful = matchedCount > 0 && liveBlocksCount > 0 && matchedCount < liveBlocksCount;

		if (!isUseful) {
			this.layout.setSubmitSampleButtonState(false, {
				hint: ''
			});
			return;
		}

		this.layout.setSubmitSampleButtonState(true, {
			disabled: false,
			title: this.__( 'Submit useful live/edit mismatch sample for mapping improvements.', 'a11yfix' ),
			hint: this.getSubmitSampleEnabledHint()
		});
	}

	async submitRulesViewSample() {
		if (this.isSubmittingSample) {
			return;
		}

		const readiness = this.buildRulesViewSampleReadiness();

		if (!readiness.isReady) {
			const readinessMessage = this.formatSampleReadinessFailureMessage(readiness);
			this.layout.setStatusError(
				`${this.__( 'Submit sample requires both live and WP editor views with captured block data.', 'a11yfix' )} ${readinessMessage}`.trim()
			);
			return;
		}

		const samplePayload = this.buildAnonymousRulesViewSamplePayload();
		this.isSubmittingSample = true;
		this.layout.setSubmitSampleButtonState(true, {
			disabled: true,
			hint: this.getSubmitSampleEnabledHint()
		});
		this.layout.setStatus(this.__( 'Submitting sample...', 'a11yfix' ));

		const submitResult = await this.sendSamplePayload(samplePayload);

		if (!submitResult.ok) {
			this.layout.setStatusError(
				`${this.__( 'Sample submission failed:', 'a11yfix' )} ${submitResult.message}`
			);
			this.isSubmittingSample = false;
			this.updateSubmitSampleButtonVisibility();
			return;
		}

		this.layout.setStatus(
			this.__( 'Sample submitted successfully. Thank you for helping us improve block mapping.', 'a11yfix' )
		);
		this.isSubmittingSample = false;
		this.updateSubmitSampleButtonVisibility();
	}

	viewRulesViewSample() {
		const readiness = this.buildRulesViewSampleReadiness();

		if (!readiness.isReady) {
			const readinessMessage = this.formatSampleReadinessFailureMessage(readiness);
			this.layout.setStatusError(
				`${this.__( 'Sample data is incomplete for sending.', 'a11yfix' )} ${readinessMessage}`.trim()
			);
			this.layout.showSubmitSamplePopup({
				schemaVersion: 2,
				generatedAt: new Date().toISOString(),
				eventType: 'rules_view_live_edit_mismatch_submit_preview',
				isUseful: false,
				readiness
			});
			return;
		}

		const samplePayload = this.buildAnonymousRulesViewSamplePayload();
		this.layout.showSubmitSamplePopup(samplePayload);
	}

	formatSampleReadinessFailureMessage(readiness) {
		const requirements = readiness?.requirements || {};
		const missing = [];

		if (requirements.hasLiveIframeUrl !== true) {
			missing.push(this.__( 'live view URL', 'a11yfix' ));
		}

		if (requirements.hasEditIframeUrl !== true) {
			missing.push(this.__( 'WP editor URL', 'a11yfix' ));
		}

		if (requirements.hasFrontendBlocks !== true) {
			missing.push(this.__( 'live blocks', 'a11yfix' ));
		}

		if (requirements.hasEditorBlocks !== true) {
			missing.push(this.__( 'editor blocks', 'a11yfix' ));
		}

		if (requirements.hasMapping !== true) {
			missing.push(this.__( 'block mapping', 'a11yfix' ));
		}

		if (missing.length === 0) {
			return '';
		}

		return `${this.__( 'Missing:', 'a11yfix' )} ${missing.join(', ')}.`;
	}

	async sendSamplePayload(samplePayload) {
		const endpoint = String(
			window?.a11yfixData?.submitSampleUrl
			|| ''
		).trim();

		if (!endpoint) {
			return {
				ok: false,
				message: this.__( 'Submit endpoint is not configured.', 'a11yfix' )
			};
		}

		const headers = {
			'Content-Type': 'application/json'
		};
		const token = String(window?.a11yfixData?.submitSampleToken || '').trim();

		if (token) {
			headers['X-A11yFix-Submit-Token'] = token;
		}

		let response;
		try {
			response = await fetch(endpoint, {
				method: 'POST',
				credentials: 'same-origin',
				headers,
				body: JSON.stringify(samplePayload)
			});
		} catch {
			return {
				ok: false,
				message: this.__( 'Network error while sending sample.', 'a11yfix' )
			};
		}

		let responseBody = null;
		try {
			responseBody = await response.json();
		} catch {
			responseBody = null;
		}

		if (!response.ok || !responseBody || responseBody.ok !== true) {
			const serverMessage = typeof responseBody?.message === 'string'
				? responseBody.message
				: `${this.__( 'HTTP status', 'a11yfix' )}: ${response.status}`;

			return {
				ok: false,
				message: serverMessage
			};
		}

		return {
			ok: true,
			file: typeof responseBody.file === 'string' ? responseBody.file : ''
		};
	}

	buildAnonymousRulesViewSamplePayload() {
		const navigationData = this.layout.getRulesViewIframeNavigationData() || {};
		const lastSnapshot = this.lastRulesViewMapSnapshot || null;
		const mode = this.resolveRulesViewMode(navigationData, lastSnapshot?.map || null);
		const {
			safeFrontendSnapshot: frontendSnapshot,
			safeEditorSnapshot: editorSnapshot,
			safeMapping: mapping
		} = this.resolveRulesViewSampleArtifacts();
		const readiness = this.buildRulesViewSampleReadiness(frontendSnapshot, editorSnapshot, mapping);
		const currentPageRules = this.currentRulesViewPage?.rules || {};
		const failedSelectorsByRule = this.currentRulesViewPage?.failedSelectorsByRule || {};
		const activeRuleKeys = Array.from(this.layout?.rulesViewHighlightController?.activeRuleHighlightKeys || []);

		const rulesSummary = {};
		for (const [ruleKey, status] of Object.entries(currentPageRules)) {
			rulesSummary[ruleKey] = {
				status: String(status || 'not_checked'),
				failedSelectorsCount: Array.isArray(failedSelectorsByRule?.[ruleKey]) ? failedSelectorsByRule[ruleKey].length : 0
			};
		}

		return {
			schemaVersion: 2,
			generatedAt: new Date().toISOString(),
			eventType: 'rules_view_live_edit_mismatch_submit_preview',
			isUseful: readiness.isReady,
			privacy: {
				isAnonymous: true,
				excludedFields: ['url', 'postId', 'rawText', 'rawHtml', 'title']
			},
			readiness,
			context: {
				mode,
				hasCurrentRulesPage: Boolean(this.currentRulesViewPage),
				activeRuleKeys
			},
			rulesSummary,
			blockInventories: {
				live: this.buildBlockInventoryFromSnapshot(frontendSnapshot, 'live'),
				edit: this.buildBlockInventoryFromSnapshot(editorSnapshot, 'edit')
			},
			mappingPairs: this.buildMappingPairs(mapping),
			mappingAlgorithmInputs: this.buildMappingAlgorithmInputs(mapping)
		};
	}

	resolveRulesViewSampleArtifacts(frontendSnapshot = null, editorSnapshot = null, mapping = null) {
		const safeFrontendSnapshot = frontendSnapshot || this.getLatestRulesViewMapSnapshotByMode('frontend');
		const safeEditorSnapshot = editorSnapshot || this.getLatestRulesViewMapSnapshotByMode('block-editor');
		let safeMapping = mapping || this.lastLiveToEditorBlockMap || null;

		if (
			(!safeMapping || safeMapping.ok === false)
			&& safeFrontendSnapshot?.map
			&& safeEditorSnapshot?.map
			&& this.layout
			&& typeof this.layout.mapLiveBlocksToEditorAndHighlight === 'function'
		) {
			const rebuiltMapping = this.layout.mapLiveBlocksToEditorAndHighlight(safeFrontendSnapshot.map);

			if (rebuiltMapping && typeof rebuiltMapping === 'object') {
				safeMapping = rebuiltMapping;
				this.lastLiveToEditorBlockMap = rebuiltMapping;
			}
		}

		return {
			safeFrontendSnapshot,
			safeEditorSnapshot,
			safeMapping
		};
	}

	buildRulesViewSampleReadiness(frontendSnapshot = null, editorSnapshot = null, mapping = null) {
		const liveNavigation = this.layout.getPreviewIframeNavigationData() || {};
		const editNavigation = this.layout.getRulesViewIframeNavigationData() || {};
		const {
			safeFrontendSnapshot,
			safeEditorSnapshot,
			safeMapping
		} = this.resolveRulesViewSampleArtifacts(frontendSnapshot, editorSnapshot, mapping);
		const hasLiveIframeUrl = this.isIframeUrlReady(liveNavigation.url) || this.isIframeUrlReady(safeFrontendSnapshot?.url || '');
		const hasEditIframeUrl = this.isIframeUrlReady(editNavigation.url) || this.isIframeUrlReady(safeEditorSnapshot?.url || '');
		const hasFrontendBlocks = this.snapshotHasBlocks(safeFrontendSnapshot)
			|| Number.isInteger(safeMapping?.liveBlocksCount) && safeMapping.liveBlocksCount > 0;
		const hasEditorBlocks = this.snapshotHasBlocks(safeEditorSnapshot)
			|| Number.isInteger(safeMapping?.editorBlocksCount) && safeMapping.editorBlocksCount > 0;
		const hasMapping = Boolean(safeMapping && safeMapping.ok !== false && Number.isInteger(safeMapping.matchedCount));

		return {
			isReady: hasLiveIframeUrl && hasEditIframeUrl && hasFrontendBlocks && hasEditorBlocks && hasMapping,
			requirements: {
				hasLiveIframeUrl,
				hasEditIframeUrl,
				hasFrontendBlocks,
				hasEditorBlocks,
				hasMapping
			},
			counts: {
				frontendBlocks: Number.isInteger(safeFrontendSnapshot?.totalBlocks) ? safeFrontendSnapshot.totalBlocks : 0,
				editorBlocks: Number.isInteger(safeEditorSnapshot?.totalBlocks) ? safeEditorSnapshot.totalBlocks : 0,
				mappedBlocks: Number.isInteger(safeMapping?.matchedCount) ? safeMapping.matchedCount : 0,
				unmatchedLiveBlocks: Number.isInteger(safeMapping?.unmatchedLiveCount) ? safeMapping.unmatchedLiveCount : 0
			}
		};
	}

	isIframeUrlReady(rawUrl) {
		const normalized = this.pageStore.normalizeUrl(rawUrl || '', window.location.origin);
		return Boolean(normalized && normalized !== 'about:blank');
	}

	snapshotHasBlocks(snapshot) {
		return Boolean(snapshot && snapshot.ok === true && Number.isInteger(snapshot.totalBlocks) && snapshot.totalBlocks > 0);
	}

	buildBlockInventoryFromSnapshot(snapshot, versionLabel) {
		if (!snapshot || typeof snapshot !== 'object' || snapshot.ok !== true) {
			return {
				ok: false,
				version: versionLabel,
				blockCount: 0,
				documents: [],
				blocks: []
			};
		}

		const blocksByType = snapshot?.map?.blocksByType || {};
		const documents = Array.isArray(snapshot?.map?.documents) ? snapshot.map.documents : [];
		const blocks = [];

		for (const [blockType, entries] of Object.entries(blocksByType)) {
			if (!Array.isArray(entries)) {
				continue;
			}

			for (const entry of entries) {
				if (!entry || typeof entry !== 'object') {
					continue;
				}

				const location = this.buildBlockLocation(entry, documents);
				blocks.push({
					index: Number.isInteger(entry.index) ? entry.index : blocks.length,
					blockType: String(blockType || 'unknown'),
					tag: String(entry.tag || ''),
					selectors: this.buildBlockSelectorHints(entry),
					identifiers: {
						id: String(entry.id || ''),
						classes: this.parseClassList(entry.className),
						matchSource: String(entry.matchSource || ''),
						failedRuleKeys: Array.isArray(entry.failedRuleKeys) ? entry.failedRuleKeys : []
					},
					location
				});
			}
		}

		return {
			ok: true,
			version: versionLabel,
			blockCount: blocks.length,
			documents: documents.map((doc, index) => ({
				index: Number.isInteger(doc?.index) ? doc.index : index,
				bodyClass: String(doc?.bodyClass || ''),
				hrefHash: this.hashForAnonymizedValue(doc?.href || '')
			})),
			blocks
		};
	}

	buildBlockLocation(entry, documents) {
		const documentIndex = Number.isInteger(entry?.documentIndex) ? entry.documentIndex : 0;
		const documentSummary = Array.isArray(documents)
			? documents.find((doc, index) => (Number.isInteger(doc?.index) ? doc.index : index) === documentIndex)
			: null;

		return {
			documentIndex,
			blockIndex: Number.isInteger(entry?.index) ? entry.index : 0,
			documentHrefHash: this.hashForAnonymizedValue(documentSummary?.href || ''),
			documentBodyClass: String(documentSummary?.bodyClass || ''),
			documentTitleHash: this.hashForAnonymizedValue(documentSummary?.title || '')
		};
	}

	parseClassList(classNameValue) {
		return String(classNameValue || '')
			.split(/\s+/)
			.map((token) => token.trim())
			.filter((token) => token.length > 0)
			.slice(0, 8);
	}

	buildBlockSelectorHints(entry) {
		const tag = String(entry?.tag || 'div').toLowerCase();
		const rawSelector = String(entry?.selector || '').trim();
		const id = String(entry?.id || '').trim();
		const classes = this.parseClassList(entry?.className || '');
		const nthIndex = Number.isInteger(entry?.index) ? entry.index + 1 : null;
		const hints = [];

		if (rawSelector) {
			hints.push(rawSelector);
		}

		if (id) {
			hints.push(`${tag}#${this.escapeSelectorToken(id)}`);
		}

		if (classes.length > 0) {
			hints.push(`${tag}.${classes.slice(0, 3).map((className) => this.escapeSelectorToken(className)).join('.')}`);
		}

		if (Number.isInteger(nthIndex) && nthIndex > 0) {
			hints.push(`${tag}:nth-of-type(${nthIndex})`);
		}

		if (hints.length === 0) {
			hints.push(tag);
		}

		return Array.from(new Set(hints));
	}

	escapeSelectorToken(value) {
		return String(value || '').replace(/([^a-zA-Z0-9_-])/g, '\\$1');
	}

	hashForAnonymizedValue(value) {
		const raw = String(value || '').trim();

		if (!raw) {
			return '';
		}

		let hash = 0;
		for (let index = 0; index < raw.length; index += 1) {
			hash = ((hash << 5) - hash) + raw.charCodeAt(index);
			hash |= 0;
		}

		return `h${Math.abs(hash).toString(36)}`;
	}

	buildMappingPairs(mapping) {
		if (!mapping || typeof mapping !== 'object' || mapping.ok === false) {
			return {
				ok: false,
				matches: [],
				unmatchedLiveBlocks: [],
				unmatchedEditBlocks: []
			};
		}

		const matches = Array.isArray(mapping.matches) ? mapping.matches : [];
		const unmatchedLiveBlocks = Array.isArray(mapping.unmatchedLiveBlocks) ? mapping.unmatchedLiveBlocks : [];
		const unmatchedEditBlocks = Array.isArray(mapping.unmatchedEditorBlocks) ? mapping.unmatchedEditorBlocks : [];

		return {
			ok: true,
			matches: matches.map((match, index) => ({
				index,
				liveIndex: Number.isInteger(match?.liveIndex) ? match.liveIndex : -1,
				editorIndex: Number.isInteger(match?.editorIndex) ? match.editorIndex : -1,
				blockType: String(match?.blockType || 'unknown'),
				canonicalBlockType: String(match?.canonicalBlockType || ''),
				threshold: Number(match?.threshold) || 0,
				score: Number(match?.score) || 0,
				scoreGap: Number(match?.scoreGap) || 0,
				candidateCount: Number(match?.candidateCount) || 0,
				highlighted: match?.highlighted === true,
				live: {
					id: String(match?.live?.id || ''),
					tag: String(match?.live?.tag || ''),
					className: String(match?.live?.className || ''),
					documentIndex: Number.isInteger(match?.live?.documentIndex) ? match.live.documentIndex : 0,
					selector: String(match?.live?.selector || ''),
					textHash: this.hashForAnonymizedValue(match?.live?.textPreview || ''),
					textLength: String(match?.live?.textPreview || '').trim().length,
					selectorHints: this.buildBlockSelectorHints(match?.live || {})
				},
				edit: {
					documentIndex: Number.isInteger(match?.editor?.documentIndex) ? match.editor.documentIndex : 0,
					id: String(match?.editor?.id || ''),
					tag: String(match?.editor?.tag || ''),
					className: String(match?.editor?.className || ''),
					selector: String(match?.editor?.selector || ''),
					textHash: this.hashForAnonymizedValue(match?.editor?.textPreview || ''),
					textLength: String(match?.editor?.textPreview || '').trim().length,
					selectorHints: this.buildBlockSelectorHints(match?.editor || {})
				}
			})),
			unmatchedLiveBlocks: unmatchedLiveBlocks.map((block, index) => ({
				index,
				liveIndex: Number.isInteger(block?.index) ? block.index : index,
				documentIndex: Number.isInteger(block?.documentIndex) ? block.documentIndex : 0,
				blockType: String(block?.blockType || block?.canonicalBlockType || 'unknown'),
				canonicalBlockType: String(block?.canonicalBlockType || ''),
				id: String(block?.id || ''),
				tag: String(block?.tag || ''),
				className: String(block?.className || ''),
				selector: String(block?.selector || ''),
				textHash: this.hashForAnonymizedValue(block?.textPreview || ''),
				textLength: String(block?.textPreview || '').trim().length,
				failedRuleKeys: Array.isArray(block?.failedRuleKeys) ? block.failedRuleKeys : [],
				selectorHints: this.buildBlockSelectorHints(block || {})
			})),
			unmatchedEditBlocks: unmatchedEditBlocks.map((block, index) => ({
				index,
				editorIndex: Number.isInteger(block?.index) ? block.index : index,
				documentIndex: Number.isInteger(block?.documentIndex) ? block.documentIndex : 0,
				blockType: String(block?.blockType || block?.canonicalBlockType || 'unknown'),
				canonicalBlockType: String(block?.canonicalBlockType || ''),
				id: String(block?.id || ''),
				tag: String(block?.tag || ''),
				className: String(block?.className || ''),
				selector: String(block?.selector || ''),
				textHash: this.hashForAnonymizedValue(block?.textPreview || ''),
				textLength: String(block?.textPreview || '').trim().length,
				selectorHints: this.buildBlockSelectorHints(block || {})
			}))
		};
	}

	buildMappingAlgorithmInputs(mapping) {
		if (!mapping || typeof mapping !== 'object') {
			return {
				ok: false,
				liveBlocksCount: 0,
				editBlocksCount: 0,
				ignoredLiveBlocksCount: 0,
				matchedCount: 0,
				unmatchedLiveCount: 0,
				unmatchedEditCount: 0
			};
		}

		return {
			ok: mapping.ok !== false,
			liveBlocksCount: Number.isInteger(mapping.liveBlocksCount) ? mapping.liveBlocksCount : 0,
			editBlocksCount: Number.isInteger(mapping.editorBlocksCount) ? mapping.editorBlocksCount : 0,
			ignoredLiveBlocksCount: Number.isInteger(mapping.ignoredLiveBlocksCount) ? mapping.ignoredLiveBlocksCount : 0,
			matchedCount: Number.isInteger(mapping.matchedCount) ? mapping.matchedCount : 0,
			unmatchedLiveCount: Number.isInteger(mapping.unmatchedLiveCount) ? mapping.unmatchedLiveCount : 0,
			unmatchedEditCount: Number.isInteger(mapping.unmatchedEditorCount) ? mapping.unmatchedEditorCount : 0
		};
	}

	summarizeSnapshot(snapshot) {
		if (!snapshot || typeof snapshot !== 'object') {
			return null;
		}

		return {
			capturedAt: snapshot.capturedAt || '',
			source: snapshot.source || '',
			mode: snapshot.mode || '',
			ok: snapshot.ok === true,
			totalBlocks: Number.isInteger(snapshot.totalBlocks) ? snapshot.totalBlocks : 0,
			blockTypesCount: Number.isInteger(snapshot.blockTypesCount) ? snapshot.blockTypesCount : 0,
			targetDocumentsCount: Number.isInteger(snapshot.targetDocumentsCount) ? snapshot.targetDocumentsCount : 0,
			excludedHeaderBlocks: Number.isInteger(snapshot.excludedHeaderBlocks) ? snapshot.excludedHeaderBlocks : 0,
			excludedFooterBlocks: Number.isInteger(snapshot.excludedFooterBlocks) ? snapshot.excludedFooterBlocks : 0,
			editorModeDivsCount: Number.isInteger(snapshot.editorModeDivsCount) ? snapshot.editorModeDivsCount : 0,
			liveBlocksComparedCount: Number.isInteger(snapshot.liveBlocksComparedCount) ? snapshot.liveBlocksComparedCount : 0,
			editorBlocksComparedCount: Number.isInteger(snapshot.editorBlocksComparedCount) ? snapshot.editorBlocksComparedCount : 0,
			matchedLiveToEditorBlocksCount: Number.isInteger(snapshot.matchedLiveToEditorBlocksCount) ? snapshot.matchedLiveToEditorBlocksCount : 0
		};
	}

	summarizeMapping(mapping) {
		if (!mapping || typeof mapping !== 'object') {
			return {
				ok: false,
				reason: 'mapping_unavailable',
				liveBlocksCount: 0,
				editorBlocksCount: 0,
				matchedCount: 0,
				unmatchedLiveCount: 0,
				highlightedMatchedCount: 0,
				scoreSummary: null,
				matchedByType: {},
				unmatchedByType: {}
			};
		}

		const matches = Array.isArray(mapping.matches) ? mapping.matches : [];
		const scores = matches.map((match) => Number(match?.score)).filter((score) => Number.isFinite(score));
		const scoreSummary = scores.length > 0
			? {
				min: Math.min(...scores),
				max: Math.max(...scores),
				avg: Number((scores.reduce((total, value) => total + value, 0) / scores.length).toFixed(2))
			}
			: null;

		const matchedByType = {};
		for (const match of matches) {
			const blockType = String(match?.blockType || 'unknown');
			if (!matchedByType[blockType]) {
				matchedByType[blockType] = { count: 0, highlightedCount: 0 };
			}

			matchedByType[blockType].count += 1;
			if (match?.highlighted === true) {
				matchedByType[blockType].highlightedCount += 1;
			}
		}

		const unmatchedByType = {};
		const unmatchedLiveBlocks = Array.isArray(mapping.unmatchedLiveBlocks) ? mapping.unmatchedLiveBlocks : [];
		for (const block of unmatchedLiveBlocks) {
			const blockType = String(block?.blockType || 'unknown');
			unmatchedByType[blockType] = (unmatchedByType[blockType] || 0) + 1;
		}

		return {
			ok: mapping.ok !== false,
			reason: String(mapping.reason || ''),
			liveBlocksCount: Number.isInteger(mapping.liveBlocksCount) ? mapping.liveBlocksCount : 0,
			editorBlocksCount: Number.isInteger(mapping.editorBlocksCount) ? mapping.editorBlocksCount : 0,
			matchedCount: Number.isInteger(mapping.matchedCount) ? mapping.matchedCount : 0,
			unmatchedLiveCount: Number.isInteger(mapping.unmatchedLiveCount) ? mapping.unmatchedLiveCount : 0,
			highlightedMatchedCount: Number.isInteger(mapping.highlightedMatchedCount) ? mapping.highlightedMatchedCount : 0,
			scoreSummary,
			matchedByType,
			unmatchedByType
		};
	}

	stopIframeTest() {
		if (!this.isRunning) {
			return;
		}

		this.shouldStopIframeRun = true;
		this.layout.setStatus(this.__( 'Stopping test...', 'a11yfix' ));
	}

	async openCrawledPageInIframe(pageIndex) {
		const page = this.crawledPages[pageIndex];

		if (!page || typeof page.url !== 'string' || page.url.length === 0) {
			return;
		}

		this.layout.showIframeTab();
		const loadResult = await this.layout.loadIframeUrl(page.url);

		if (!loadResult?.loaded) {
			this.layout.setStatusError(this.__( 'Iframe run warning for URL:', 'a11yfix' ) + ` ${page.url}`);
			return;
		}

		const normalizedPostId = this.pageStore.normalizePostId(loadResult.postId ?? page.postId);
		if (normalizedPostId) {
			this.crawledPages = this.pageStore.updateCrawledPagePostId(this.crawledPages, page.url, normalizedPostId);
			this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
			this.layout.renderCrawledPages(this.crawledPages);
		}

		this.layout.setStatus(page.url);
	}

	async openRuleDetails(pageIndex, ruleKey) {
		const page = await this.ensurePageRuleDataLoaded(pageIndex);

		if (!page) {
			return;
		}

		if (page?.rules?.[ruleKey] === 'skipped') {
			return;
		}

		if (ruleKey === 'imageAlt') {
			const images = page?.ruleDetails?.imageAlt?.images;

			if (Array.isArray(images) && images.length === 0) {
				return;
			}

			this.layout.showImageAltPopup(page);
			return;
		}

		if (ruleKey === 'linkTitle') {
			this.layout.showLinksPopup(page);
			return;
		}

		if (ruleKey === 'headingOrder') {
			this.layout.showHeadingPopup(page);
			return;
		}

		if (ruleKey === 'formLabels') {
			this.layout.showFormLabelsPopup(page);
			return;
		}

		if (ruleKey === 'accessibleNamePreferredSource') {
			this.layout.showAccessibleNamePreferredSourcePopup(page);
			return;
		}

		if (ruleKey === 'ariaLabelledbyIntegrity') {
			this.layout.showAriaLabelledbyPopup(page);
			return;
		}

		if (ruleKey === 'ariaHiddenInteractive') {
			this.layout.showAriaHiddenInteractivePopup(page);
			return;
		}

		if (ruleKey === 'keyboardFocusable') {
			this.layout.showKeyboardFocusablePopup(page);
			return;
		}

		if (ruleKey === 'lowColorContrast') {
			this.layout.showLowContrastPopup(page);
		}
	}

	clearCrawledPages() {
		const removed = this.localStorageService.remove(this.crawledPathsStorageKey);
		this.localStorageService.remove(this.layoutDetectionStorageKey);
		this.crawledPages = [];
		this.layoutDetection = this.getDefaultLayoutDetection();
		this.layout.setCommonLayoutDetection(this.layoutDetection);
		this.layout.renderCrawledPages(this.crawledPages);

		if (removed) {
			this.layout.setStatus(this.__( 'Crawled pages cleared.', 'a11yfix' ));
			return;
		}

		this.layout.setStatus(this.__( 'Could not clear crawled pages.', 'a11yfix' ));
	}

	clearRulesResults() {
		const removed = this.localStorageService.remove(this.rulesResultsStorageKey);
		this.pageStore.removeRuleResultDetails(this.rulesResultsDetailsStorageKey);
		this.hasRulesCheckStarted = false;
		this.ruleResultPages = [];
		this.layoutDetection = this.loadLayoutDetection();
		this.layout.setCommonLayoutDetection(this.layoutDetection);
		this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
		this.layout.renderCrawledPages(this.crawledPages);
		this.layout.renderPagesWithRules([]);
		window.clearTimeout(this.ruleDataPrefetchTimerId);
		this.ruleDataPrefetchInFlightByUrl.clear();

		if (removed) {
			this.layout.setStatus(this.__( 'Crawled pages + rules data cleared.', 'a11yfix' ));
			return;
		}

		this.layout.setStatus(this.__( 'Could not clear crawled pages + rules data.', 'a11yfix' ));
	}

	async runCrawledPagesInIframe() {
		if (this.isRunning || this.crawledPages.length === 0) {
			return;
		}

		if (!this.ruleRunner) {
			this.layout.setStatus(this.__( 'Rule runner is not configured.', 'a11yfix' ));
			return;
		}

		this.isRunning = true;
		this.shouldStopIframeRun = false;
		this.hasRulesCheckStarted = true;
		this.localStorageService.remove(this.rulesResultsStorageKey);
		this.pageStore.removeRuleResultDetails(this.rulesResultsDetailsStorageKey);
		this.ruleResultPages = [];
		this.pages = [];
		this.layout.renderPagesWithRules([]);
		window.clearTimeout(this.ruleDataPrefetchTimerId);
		this.ruleDataPrefetchInFlightByUrl.clear();
		this.layout.setBusyState(true);
		this.layout.setTestingState(true);
		this.layout.showIframeTab();
		this.layout.clearIframe();
		let completedPagesSinceSummarySave = 0;
		let completedPagesSinceFullRender = 0;
		const summarySaveBatchSize = 5;
		const fullRenderBatchSize = 8;

		try {
 			const runResult = await this.iframeTestRunner.run(this.crawledPages, {
				shouldStop: () => this.shouldStopIframeRun,
				onPageInitialize: ({ pageIndex, totalPages, page, pages }) => {
					this.pages = pages;

					if (pageIndex === 0) {
						this.layout.renderPagesWithRules(this.pages);
					}

					this.layout.setStatus(
						`${this.__( 'Iframe run in progress... Processing:', 'a11yfix' )} ${pageIndex + 1}/${totalPages}`
					);
				},
				onPagePostId: ({ pageUrl, postId }) => {
					this.crawledPages = this.pageStore.updateCrawledPagePostId(this.crawledPages, pageUrl, postId);
					this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
					this.layout.renderCrawledPages(this.crawledPages);
				},
				onPageComplete: ({ pageIndex, page, pages, documentNode }) => {
					const rulePartsByRule = this.rulePartClassifier.classifyPageRuleParts(page, documentNode);
					const pageWithRuleParts = {
						...page,
						rulePartsByRule
					};

					this.pages = pages;
					this.pages[pageIndex] = pageWithRuleParts;
					this.pageStore.saveRuleResultPageDetails(this.rulesResultsDetailsStorageKey, pageWithRuleParts);

					completedPagesSinceSummarySave += 1;
					if (completedPagesSinceSummarySave >= summarySaveBatchSize) {
						this.pageStore.saveRuleResultPagesSummary(this.rulesResultsStorageKey, this.pages);
						completedPagesSinceSummarySave = 0;
					}

					completedPagesSinceFullRender += 1;
					if (pageIndex === 0 || completedPagesSinceFullRender >= fullRenderBatchSize) {
						this.layout.renderPagesWithRules(this.pages);
						completedPagesSinceFullRender = 0;
						return;
					}

					this.layout.updateRuleStatus(pageIndex, pageWithRuleParts);
				},
				onPageWarning: ({ pageUrl, reason, timeoutMs }) => {
					if (reason === 'timeout') {
						this.layout.setStatus(
							`${this.__( 'Iframe test timed out and was skipped for URL:', 'a11yfix' )} ${pageUrl} (${Math.round((timeoutMs || 0) / 1000)}s)`
						);
						return;
					}

					this.layout.setStatus(
						`${this.__( 'Iframe run warning for URL:', 'a11yfix' )} ${pageUrl}`
					);
				}
			});

			this.pages = runResult.pages;

			this.ruleResultPages = this.ruleRunner.normalizePages(this.pages);
			this.crawledPages = this.pageStore.syncCrawledPagesWithRulePostIds(this.crawledPages, this.ruleResultPages);
			this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
			this.layout.renderCrawledPages(this.crawledPages);
			this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
			this.pageStore.saveRuleResultPages(this.rulesResultsStorageKey, this.pages, this.rulesResultsDetailsStorageKey);
			this.scheduleRuleDataPrefetch(5);

			if (this.shouldStopIframeRun) {
				this.layout.setStatus(this.__( 'Test stopped.', 'a11yfix' ));
			} else if (runResult.timeoutCount > 0) {
				this.layout.setStatus(
					`${this.__( 'Test complete. Timed out pages skipped:', 'a11yfix' )} ${runResult.timeoutCount}`
				);
			} else {
				this.layout.setStatus(this.__( 'Test complete', 'a11yfix' ));
			}
		} catch (error) {
			this.layout.setStatus(
				`${this.__( 'Iframe run failed:', 'a11yfix' )} ${error instanceof Error ? error.message : this.__( 'Unknown error', 'a11yfix' )}`
			);
		} finally {
			this.isRunning = false;
			this.shouldStopIframeRun = false;
			this.layout.setTestingState(false);
			this.layout.setBusyState(false);
		}
	}

	runCurrentIframePageInIframe() {
		if (this.isRunning) {
			return;
		}

		if (!Array.isArray(this.crawledPages) || this.crawledPages.length === 0) {
			this.layout.setStatus(this.__( 'No crawled pages available.', 'a11yfix' ));
			return;
		}

		if (!this.ruleRunner) {
			this.layout.setStatus(this.__( 'Rule runner is not configured.', 'a11yfix' ));
			return;
		}

		const currentIframeUrl = this.pageStore.normalizeUrl(this.layout?.previewIframe?.src || '');

		if (!currentIframeUrl || currentIframeUrl === 'about:blank') {
			this.layout.setStatus(this.__( 'Open a crawled page in Test before running a single-page test.', 'a11yfix' ));
			return;
		}

		const targetPage = this.crawledPages.find((page) => this.pageStore.normalizeUrl(page.url) === currentIframeUrl);

		if (!targetPage) {
			this.layout.setStatus(this.__( 'Current iframe page is not in crawled pages.', 'a11yfix' ));
			return;
		}

		this.runSingleIframePage(targetPage);
	}

	async runSingleIframePage(targetPage) {
		this.isRunning = true;
		this.shouldStopIframeRun = false;
		this.hasRulesCheckStarted = true;
		this.layout.setBusyState(true);
		this.layout.setTestingState(true);
		this.layout.showIframeTab();

		try {
			const runResult = await this.iframeTestRunner.run([targetPage], {
				shouldStop: () => this.shouldStopIframeRun,
				onPageInitialize: ({ pageIndex, totalPages }) => {
					this.layout.setStatus(
						`${this.__( 'Iframe run in progress... Processing:', 'a11yfix' )} ${pageIndex + 1}/${totalPages}`
					);
				},
				onPagePostId: ({ pageUrl, postId }) => {
					this.crawledPages = this.pageStore.updateCrawledPagePostId(this.crawledPages, pageUrl, postId);
					this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
					this.layout.renderCrawledPages(this.crawledPages);
				},
				onPageComplete: ({ pageIndex, page, pages, documentNode }) => {
					const rulePartsByRule = this.rulePartClassifier.classifyPageRuleParts(page, documentNode);
					const pageWithRuleParts = {
						...page,
						rulePartsByRule
					};

					pages[pageIndex] = pageWithRuleParts;
				},
				onPageWarning: ({ pageUrl, reason, timeoutMs }) => {
					if (reason === 'timeout') {
						this.layout.setStatus(
							`${this.__( 'Iframe test timed out and was skipped for URL:', 'a11yfix' )} ${pageUrl} (${Math.round((timeoutMs || 0) / 1000)}s)`
						);
						return;
					}

					this.layout.setStatus(
						`${this.__( 'Iframe run warning for URL:', 'a11yfix' )} ${pageUrl}`
					);
				}
			});

			const [updatedSinglePage] = runResult.pages;

			if (updatedSinglePage) {
				const normalizedTargetUrl = this.pageStore.normalizeUrl(updatedSinglePage.url);
				const existingRulePages = this.ruleRunner.normalizePages(this.ruleResultPages || []);
				const updatedRulePages = existingRulePages.map((page) => {
					if (this.pageStore.normalizeUrl(page.url) !== normalizedTargetUrl) {
						return page;
					}

					return updatedSinglePage;
				});

				if (!updatedRulePages.some((page) => this.pageStore.normalizeUrl(page.url) === normalizedTargetUrl)) {
					updatedRulePages.push(updatedSinglePage);
				}

				this.ruleResultPages = this.ruleRunner.normalizePages(updatedRulePages);
				this.crawledPages = this.pageStore.syncCrawledPagesWithRulePostIds(this.crawledPages, this.ruleResultPages);
				this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
				this.layout.renderCrawledPages(this.crawledPages);
				this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
				this.pageStore.saveRuleResultPages(this.rulesResultsStorageKey, this.pages, this.rulesResultsDetailsStorageKey);
				this.layout.renderPagesWithRules(this.pages);
				this.scheduleRuleDataPrefetch(5);
			}

			if (this.shouldStopIframeRun) {
				this.layout.setStatus(this.__( 'Test stopped.', 'a11yfix' ));
			} else if (runResult.timeoutCount > 0) {
				this.layout.setStatus(
					`${this.__( 'Test complete. Timed out pages skipped:', 'a11yfix' )} ${runResult.timeoutCount}`
				);
			} else {
				this.layout.setStatus(this.__( 'Test complete', 'a11yfix' ));
			}
		} catch (error) {
			this.layout.setStatus(
				`${this.__( 'Iframe run failed:', 'a11yfix' )} ${error instanceof Error ? error.message : this.__( 'Unknown error', 'a11yfix' )}`
			);
		} finally {
			this.isRunning = false;
			this.shouldStopIframeRun = false;
			this.layout.setTestingState(false);
			this.layout.setBusyState(false);
		}
	}

	async start() {
		if (this.isRunning) {
			return;
		}

		const depthLimit = this.layout.getDepthLimit();
		this.hasRulesCheckStarted = false;
		this.layout.renderPagesWithRules([]);
		this.isRunning = true;
		this.layout.setBusyState(true);
		this.layout.setStatus(this.__( 'Crawling in progress...', 'a11yfix' ));

		try {
			const crawledPages = await this.crawlEngine.crawl(this.homeUrl, depthLimit, (progressPages) => {
				this.crawledPages = this.pageStore.normalizeCrawledPages(progressPages);
				this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
				this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
				this.layout.renderCrawledPages(this.crawledPages);
				this.layout.setStatus(
					`${this.__( 'Crawling in progress... Pages found:', 'a11yfix' )} ${progressPages.length}`
				);
			});
			this.layoutDetection = this.commonLayoutDetector.computeConsensus(crawledPages);
			this.saveLayoutDetection(this.layoutDetection);
			this.layout.setCommonLayoutDetection(this.layoutDetection);
			this.crawledPages = this.pageStore.normalizeCrawledPages(crawledPages);
			this.pageStore.saveCrawledPages(this.crawledPathsStorageKey, this.crawledPages);
			this.pages = this.pageStore.mergeCrawledPagesWithRuleResults(this.crawledPages, this.ruleResultPages);
			this.layout.renderCrawledPages(this.crawledPages);
			const layoutSummary = this.buildLayoutDetectionSummary(this.layoutDetection);
			this.layout.setStatus(
				`${this.__( 'Crawl complete. Pages found:', 'a11yfix' )} ${crawledPages.length}. ${layoutSummary}`
			);
		} catch (error) {
			this.layout.setStatus(
				`${this.__( 'Crawl failed:', 'a11yfix' )} ${error instanceof Error ? error.message : this.__( 'Unknown error', 'a11yfix' )}`
			);
		} finally {
			this.isRunning = false;
			this.layout.setBusyState(false);
		}
	}

	async runRulesCheck() {
		if (this.isRunning) {
			return;
		}

		this.layout.setStatus(this.__( 'Use "Run crawled pages in iframe" to start rules check.', 'a11yfix' ));
	}

	async crawl(startUrl, depthLimit, onPageFound) {
		return this.crawlEngine.crawl(startUrl, depthLimit, onPageFound);
	}

	async fetchPage(url) {
		const response = await fetch(url, {
			method: 'GET',
			credentials: 'same-origin',
			redirect: 'follow'
		});

		if (!response.ok) {
			throw new Error(`${this.__( 'Request failed', 'a11yfix' )} (${response.status})`);
		}

		const contentType = String(response.headers.get('content-type') || '').toLowerCase();
		const isHtmlResponse = contentType.includes('text/html') || contentType.includes('application/xhtml+xml');

		if (!isHtmlResponse) {
			throw new Error('non_html_response');
		}

		const html = await response.text();
		const pageTypeHeader = String(response.headers.get('X-A11yFix-Page-Type') || '').toLowerCase();
		const backendPageType = this.normalizeBackendPageType(pageTypeHeader);
		const pageType = backendPageType || this.detectPageTypeBeforePostId(url, html);
		const shouldSkipPostIdDetection = pageType === 'home' || pageType === 'archive';
		const postIdHeader = shouldSkipPostIdDetection ? null : response.headers.get('X-A11yFix-Post-Id');

		return {
			html,
			postId: this.pageStore.normalizePostId(postIdHeader),
			pageType,
			layoutSignals: this.commonLayoutDetector.detectPageLayout(html)
		};
	}

	normalizeBackendPageType(rawPageType) {
		if (rawPageType === 'home' || rawPageType === 'archive' || rawPageType === 'singular') {
			return rawPageType;
		}

		return '';
	}

	detectPageTypeBeforePostId(url, html) {
		const bodyClassTokens = this.extractBodyClassTokens(html);

		if (this.isHomePageBySignals(url, bodyClassTokens)) {
			return 'home';
		}

		if (this.isArchivePageBySignals(url, bodyClassTokens)) {
			return 'archive';
		}

		return 'singular';
	}

	extractBodyClassTokens(html) {
		if (typeof html !== 'string' || html.length === 0) {
			return [];
		}

		const bodyClassMatch = html.match(/<body[^>]*class=(['"])(.*?)\1/i);
		const bodyClassValue = bodyClassMatch?.[2] || '';

		if (!bodyClassValue) {
			return [];
		}

		return bodyClassValue
			.split(/\s+/)
			.map((token) => token.trim().toLowerCase())
			.filter((token) => token.length > 0);
	}

	isHomePageBySignals(rawUrl, bodyClassTokens) {
		const classTokenSet = new Set(Array.isArray(bodyClassTokens) ? bodyClassTokens : []);

		if (classTokenSet.has('home') || classTokenSet.has('front-page')) {
			return true;
		}

		const normalizedUrl = this.pageStore.normalizeUrl(rawUrl, this.homeUrl);
		return Boolean(normalizedUrl && normalizedUrl === this.homeUrl);
	}

	isArchivePageBySignals(rawUrl, bodyClassTokens) {
		const classTokenSet = new Set(Array.isArray(bodyClassTokens) ? bodyClassTokens : []);
		const archiveClassPrefixes = [
			'category-',
			'tag-',
			'tax-',
			'author-',
			'date-',
			'post-type-archive'
		];

		for (const token of classTokenSet) {
			if (
				token === 'archive'
				|| token === 'category'
				|| token === 'tag'
				|| token === 'author'
				|| token === 'date'
				|| token === 'search'
			) {
				return true;
			}

			if (archiveClassPrefixes.some((prefix) => token.startsWith(prefix))) {
				return true;
			}
		}

		try {
			const parsed = new URL(rawUrl, this.homeUrl);
			const path = parsed.pathname.toLowerCase();

			if (/\/page\/\d+\/?$/.test(path)) {
				return true;
			}

			return /(\/category\/|\/tag\/|\/author\/|\/date\/|\/archive\/|\/search\/)/.test(path);
		} catch {
			return false;
		}
	}

	getDefaultLayoutDetection() {
		return {
			totalPages: 0,
			header: {
				detected: false,
				selector: '',
				fingerprint: '',
				confidence: 0,
				presenceRatio: 0,
				matchedPages: 0,
				consideredPages: 0
			},
			footer: {
				detected: false,
				selector: '',
				fingerprint: '',
				confidence: 0,
				presenceRatio: 0,
				matchedPages: 0,
				consideredPages: 0
			}
		};
	}

	loadLayoutDetection() {
		const parsed = this.localStorageService.getJson(this.layoutDetectionStorageKey, null);

		if (!parsed || typeof parsed !== 'object') {
			return this.getDefaultLayoutDetection();
		}

		return {
			...this.getDefaultLayoutDetection(),
			...parsed,
			header: {
				...this.getDefaultLayoutDetection().header,
				...(parsed.header || {})
			},
			footer: {
				...this.getDefaultLayoutDetection().footer,
				...(parsed.footer || {})
			}
		};
	}

	saveLayoutDetection(layoutDetection) {
		this.localStorageService.setJson(this.layoutDetectionStorageKey, layoutDetection || this.getDefaultLayoutDetection());
	}

	buildLayoutDetectionSummary(layoutDetection) {
		const safeDetection = layoutDetection || this.getDefaultLayoutDetection();
		const headerText = safeDetection.header?.detected
			? `${this.__( 'header detected', 'a11yfix' )} (${Math.round((safeDetection.header.confidence || 0) * 100)}%)`
			: this.__( 'header not detected', 'a11yfix' );
		const footerText = safeDetection.footer?.detected
			? `${this.__( 'footer detected', 'a11yfix' )} (${Math.round((safeDetection.footer.confidence || 0) * 100)}%)`
			: this.__( 'footer not detected', 'a11yfix' );

		return `${this.__( 'Common layout analysis:', 'a11yfix' )} ${headerText}, ${footerText}.`;
	}
}
